import type { MedicationPadItemType } from '@/components/Patient/components/PrescriptionPad';
import { PrescriptionPadStep } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import { appendDosageSuffix, removeDosageSuffix } from '@/utils/dosageLimit/dosageSuffix';
import type { DispenseLimitRow } from '@/utils/dosageLimit/getFormulationDispenseLimitRows';
import { getPadItemDosageSuffix } from '@/utils/dosageLimit/getPadItemDosageSuffix';
import type { PrescriptionPadRules } from '../types';

/**
 * The dispense limits are edited as chips beside the dosage text, but the prescription only carries the dosage
 * text, so their phrases are written into it for the review step and taken back out when returning to edit.
 *
 * Taking them out recomputes the suffix and strips it only if the dosage still ends with it, which holds because the
 * pad is read-only at the review step (`PrescriptionPadWrapper` renders it only at Create). Anything that edits a pad
 * item at the review step must strip the written suffix first, or returning to edit leaves the old one in the dosage
 * and the next review step appends a second.
 */
export const syncDosageSuffixToStep = (
  item: MedicationPadItemType,
  dispenseLimitRows: DispenseLimitRow[],
  rules: PrescriptionPadRules,
  step: PrescriptionPadStep,
): MedicationPadItemType => {
  const detail = item.medicationPadItem;
  const isWriting = step === PrescriptionPadStep.Confirm;
  // Taken out under the rules it was written with: a flip at the review step goes through `updatePrescriptionPadRules`,
  // which takes it out under the old rules first
  const suffix = getPadItemDosageSuffix(item, dispenseLimitRows, rules);
  if (!detail || !suffix) {
    return item;
  }
  const dosage = isWriting ? appendDosageSuffix(detail.dosage, suffix) : removeDosageSuffix(detail.dosage, suffix);
  return { ...item, medicationPadItem: { ...detail, dosage } };
};
